import { OneWayInServiceUpsertDto } from 'src/api/v2';
import useServiceSubForm, { extractSubData, SubFormProps, SubFormReturn } from '../use-service-sub-form';
import { formatDateInputDate } from 'src/utils/FormatValue';
import { useTranslation } from 'react-i18next';

export type AboutFields =
  | 'name'
  | 'validFrom'
  | 'validTo'
  | 'description'
  | 'customerDescription'
  | 'servicePurpose'
  | 'keywords'
  | 'externalLinks';
export type AboutData = Pick<OneWayInServiceUpsertDto, AboutFields>;

const useAboutForm = ({
  data,
  updateData,
  errors,
  setErrors,
}: SubFormProps<AboutData>): SubFormReturn<AboutData, AboutFields> => {
  const { t } = useTranslation('services');
  const rules: Partial<Record<AboutFields, any>> = {
    name: {
      required: t('about.serviceName.required'),
      maxLength: { value: 500, message: t('about.serviceName.maxLength') },
    },
    validFrom: {
      required: t('about.validFrom.required'),
      setValueAs: (value) => (value === '' ? undefined : value),
    },
    validTo: {
      required: t('about.validTo.required'),
      setValueAs: (value) => (value === '' ? undefined : value),
    },
    servicePurpose: {
      required: t('about.purpose.required'),
      maxLength: { value: 500, message: t('about.purpose.maxLength') },
    },
    description: {
      validate: (value) => {
        if (!value || value === '' || value === '<p><br></p>') {
          return t('about.description.required');
        }
        return true;
      },
    },
    keywords: {
      validate: (value) => {
        if (!value || !Array.isArray(value) || value.length === 0) {
          return t('about.keywords.required');
        }
        return true;
      },
    },
  };

  const extractAboutFields = (data: OneWayInServiceUpsertDto): AboutData => {
    return extractSubData(data, [
      'name',
      'validFrom',
      'validTo',
      'description',
      'customerDescription',
      'servicePurpose',
      'keywords',
      'externalLinks',
    ]);
  };

  const aboutFields = extractAboutFields(data);
  const formattedFields = {
    ...aboutFields,
    validFrom: aboutFields.validFrom ? (formatDateInputDate(aboutFields.validFrom) as any) : undefined,
    validTo: aboutFields.validTo ? (formatDateInputDate(aboutFields.validTo) as any) : undefined,
  };
  const { form } = useServiceSubForm<AboutData>(formattedFields, updateData, errors, setErrors);
  const registerWithRules = (field: AboutFields) => form.register(field, rules[field]);
  return { form, rules, registerWithRules };
};

export default useAboutForm;
